import React, { useEffect, useState, useRef } from 'react';
import Chart from "react-apexcharts";
import { Grid, Skeleton, Typography, Box } from '@mui/material';
import axios from 'src/utils/axios';
import SubCard from 'src/components/cards/SubCard';

const ServiceRejectRate = ({ ServiceShopId }) => {
    const [labelsLastYear, setLabelsLastYear] = useState([]);
    const [seriesLastYear, setSeriesLastYear] = useState([]);
    const [labelsThisYear, setLabelsThisYear] = useState([]);
    const [seriesThisYear, setSeriesThisYear] = useState([]);
    const [lastYearTitle, setLastYearTitle] = useState('');
    const [thisYearTitle, setThisYearTitle] = useState('');
    const [lastYearRateTitle, setLastYearRateTitle] = useState('');
    const [thisYearRateTitle, setThisYearRateTitle] = useState('');
    const [totalAccountedServicePaperLastYear, setTotalAccountedServicePaperLastYear] = useState(0);
    const [totalAccountedServicePaperThisYear, setTotalAccountedServicePaperThisYear] = useState(0);

    const [loading, setLoading] = useState(true);
    const [chartHeight, setChartHeight] = useState(400); // Varsayılan yükseklik

    const lastYearChartRef = useRef(null);
    const thisYearChartRef = useRef(null);

    useEffect(() => {
        axios.get(`GetServiceRejectRate/${ServiceShopId}`)
            .then((response) => {
                if (response) {
                    const data = response.data;
                    const sortedRejectsLastYear = getSortedTop10(data.LastYearReject);
                    const sortedRejectsThisYear = getSortedTop10(data.ThisYearReject);

                    setLabelsLastYear(sortedRejectsLastYear.map((item) => capitalizeFirstLetter(item.RedNedeni)));
                    setSeriesLastYear(sortedRejectsLastYear.map((item) => item.RedCount));
                    setLabelsThisYear(sortedRejectsThisYear.map((item) => capitalizeFirstLetter(item.RedNedeni)));
                    setSeriesThisYear(sortedRejectsThisYear.map((item) => item.RedCount));
                    setLastYearTitle(`01.01.${data.LastYearTodayDate.split('.')[2]} - ${data.LastYearTodayDate} Red Nedenleri`);
                    setThisYearTitle(`01.01.${data.TodayDate.split('.')[2]} - ${data.TodayDate} Red Nedenleri`);
                    setLastYearRateTitle(`01.01.${data.LastYearTodayDate.split('.')[2]} - ${data.LastYearTodayDate} Red Oranı`);
                    setThisYearRateTitle(`01.01.${data.TodayDate.split('.')[2]} - ${data.TodayDate} Red Oranı`);
                    setTotalAccountedServicePaperLastYear(data.TotalAccountedServicePaperLastYear);
                    setTotalAccountedServicePaperThisYear(data.TotalAccountedServicePaperThisYear);

                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('API error:', error);
                setLoading(false);
            });
    }, [ServiceShopId]);

    useEffect(() => {
        // Grafiklerin yüksekliğini güncelle
        if (lastYearChartRef.current && thisYearChartRef.current) {
            const lastYearHeight = lastYearChartRef.current.offsetHeight;
            const thisYearHeight = thisYearChartRef.current.offsetHeight;
            setChartHeight(Math.max(lastYearHeight, thisYearHeight));
        }
    }, [labelsLastYear, seriesLastYear, labelsThisYear, seriesThisYear]);

    const getOptions = (title, label) => {
        return {
            chart: {
                type: 'donut',
                width: '100%',
                height: chartHeight, // Dinamik yükseklik
            },
            plotOptions: {
                pie: {
                    startAngle: -90,
                    endAngle: 270,
                    donut: {
                        size: '70%',
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: 'Toplam',
                                formatter: function (w) {
                                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                }
                            }
                        }
                    }
                }
            },
            labels: label.length > 0 ? label : ["No data available"],
            dataLabels: {
                enabled: true,
            },
            fill: {
                type: 'gradient',
            },
            colors: [
                "#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0",
                "#33b2df", "#546E7A", "#d4526e", "#13d8aa", "#A5978B",
                "#2b908f", "#f9a3a4", "#90ee7e", "#fa4443", "#69d2e7",
                "#FF5733", "#F5FF33", "#8D33FF", "#FF5733", "#33FFA5"
            ],
            title: {
                text: title,
            },
            legend: {
                formatter: function (seriesName, opts) {
                    return seriesName.length > 50 ? seriesName.substring(0, 50) + '...' : seriesName;
                },
            },
            responsive: [{
                breakpoint: 480,
                options: {
                    chart: {
                        width: '100%',
                        height: 300, // Küçük ekranlarda daha küçük yükseklik
                    },
                    legend: {
                        position: 'bottom',
                    },
                },
            }],
        };
    }

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toLocaleUpperCase('tr') + string.slice(1);
    }

    const getSortedTop10 = (data) => {
        // Verileri sırala ve ilk 10 elemanı döndür. Diğer elemanları topla ve "Diğer" olarak ekle
        const sortedData = (data || []).sort((a, b) => b.RedCount - a.RedCount);
        const top10 = sortedData.slice(0, 10);
        const others = sortedData.slice(10);
        const othersCount = others.reduce((a, b) => a + b.RedCount, 0);
        if (othersCount > 0) {
            top10.push({ RedNedeni: 'Diğer', RedCount: othersCount });
        }
        return top10;
    }

    const numberToString = (number) => {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }

    const getRadialBarOptions = (title) => {
        return {
            series: [0],
            chart: {
                height: 220,
                type: 'radialBar',
                offsetY: -10
            },
            plotOptions: {
                radialBar: {
                    startAngle: -135,
                    endAngle: 135,
                    hollow: {
                        margin: 0,
                        size: '70%',
                        background: '#fff',
                        image: undefined,
                        imageOffsetX: 0,
                        imageOffsetY: 0,
                        imageWidth: 150,
                        imageHeight: 150,
                        imageClipped: true,
                        position: 'front'
                    },
                    track: {
                        background: '#e7e7e7',
                        strokeWidth: '97%',
                        margin: 5, // margin is in pixels
                        dropShadow: {
                            enabled: true,
                            top: 2,
                            left: 0,
                            blur: 4,
                            opacity: 0.15
                        }
                    },
                    dataLabels: {
                        name: {
                            show: true,
                        },
                        value: {
                            show: true,
                            fontSize: '28px',
                            color: '#000000',
                            offsetY: 28,
                            formatter: function (val) {
                                return val + '%';
                            }
                        }
                    },

                }
            },
            labels: ['Red Oranı'],
            title: {
                text: title
            }

        };
    }


    return (
        <Grid container spacing={3}>
            {loading ? (
                <>
                    <Grid
                        style={{ width: '100%' }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <Skeleton variant="rectangular" width="100%" height={chartHeight} />
                    </Grid>
                    <Grid
                        style={{ width: '100%' }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <Skeleton variant="rectangular" width="100%" height={chartHeight} />
                    </Grid>
                </>
            ) : (
                <>
                    <Grid
                        style={{ display: 'flex', flexDirection: 'column' }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <Grid container spacing={5} style={{ flexGrow: 1 }}>
                            <Grid style={{ flexGrow: 1 }} size={12}>
                                <SubCard>
                                    <Chart
                                        options={getOptions(lastYearTitle, labelsLastYear)}
                                        series={seriesLastYear.length > 0 ? seriesLastYear : [1]}
                                        type='donut'
                                        height={350}
                                    />
                                </SubCard>
                            </Grid>
                            <Grid style={{ flexGrow: 1 }} size={12}>
                                <SubCard>
                                    <Chart
                                        options={getRadialBarOptions(lastYearRateTitle)}
                                        series={[totalAccountedServicePaperLastYear > 0 ? ((seriesLastYear.reduce((a, b) => a + b, 0) * 100) / totalAccountedServicePaperLastYear).toFixed(2) : '0']}
                                        type='radialBar'
                                        height={220}
                                    />
                                    <Box display="flex" flexDirection={'column'} alignItems={'center'}>
                                        <Typography variant="h6" gutterBottom textAlign={'left'}>
                                            <b>Toplam Red Adet: </b>{numberToString(seriesLastYear.reduce((a, b) => a + b, 0))}
                                        </Typography>
                                        <Typography variant="h6" gutterBottom textAlign={'left'}>
                                            <b>Toplam Muhasebeleşmiş Fiş: </b>{numberToString(totalAccountedServicePaperLastYear)}
                                        </Typography>
                                    </Box>
                                </SubCard>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        style={{ display: 'flex', flexDirection: 'column' }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <Grid container spacing={5} style={{ flexGrow: 1 }}>
                            <Grid style={{ flexGrow: 1 }} size={12}>
                                <SubCard>
                                    <Chart
                                        options={getOptions(thisYearTitle, labelsThisYear)}
                                        series={seriesThisYear.length > 0 ? seriesThisYear : [1]}
                                        type='donut'
                                        height={350}
                                    />
                                </SubCard>
                            </Grid>
                            <Grid style={{ flexGrow: 1 }} size={12}>
                                <SubCard>
                                    <Chart
                                        options={getRadialBarOptions(thisYearRateTitle)}
                                        series={[totalAccountedServicePaperThisYear > 0 ? ((seriesThisYear.reduce((a, b) => a + b, 0) * 100) / totalAccountedServicePaperThisYear).toFixed(2) : '0']}
                                        type='radialBar'
                                        height={220}
                                    />
                                    <Box display="flex" flexDirection={'column'} alignItems={'center'}>
                                        <Typography variant="h6" gutterBottom textAlign={'left'}>
                                            <b>Toplam Red Adet: </b>{numberToString(seriesThisYear.reduce((a, b) => a + b, 0))}
                                        </Typography>
                                        <Typography variant="h6" gutterBottom textAlign={'left'}>
                                            <b>Toplam Muhasebeleşmiş Fiş: </b>{numberToString(totalAccountedServicePaperThisYear)}
                                        </Typography>
                                    </Box>
                                </SubCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default ServiceRejectRate;
